import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { AnimatePresence, motion, useScroll } from 'framer-motion';

import {
  dropdownVariants,
  interactiveHoverVariants,
  pulseVariants,
  wiggleVariants,
} from '@/animations';
import { Button, LazyImage } from '@/components';
import { NAV_CONTENT } from '@/content';
import { useAuth } from '@/context/AuthContext';
import { useCartCount } from '@/context/CartCountContext';
import { useTheme } from '@/context/ThemeContext';
import { useWishlistCount } from '@/context/WishlistCountContext';
import { playAudio } from '@/utils/audio';
import { Icon } from '@/utils/icons';
const MobileMenu = React.lazy(() => import('@/components/MobileMenu'));
const SearchModal = React.lazy(() => import('@/components/SearchModal'));

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount: cartCount } = useCartCount();
  const { itemCount: wishlistCount } = useWishlistCount();
  const { resolvedTheme, setTheme } = useTheme();

  const [shouldWiggleCart, setShouldWiggleCart] = useState(false);
  const [shouldWiggleWishlist, setShouldWiggleWishlist] = useState(false);

  useEffect(() => {
    if (cartCount > 0) {
      const wiggleTimer = setTimeout(() => setShouldWiggleCart(true), 0);
      const stopTimer = setTimeout(() => setShouldWiggleCart(false), 600);
      return () => {
        clearTimeout(wiggleTimer);
        clearTimeout(stopTimer);
      };
    }
  }, [cartCount]);

  useEffect(() => {
    if (wishlistCount > 0) {
      const wiggleTimer = setTimeout(() => setShouldWiggleWishlist(true), 0);
      const stopTimer = setTimeout(() => setShouldWiggleWishlist(false), 600);
      return () => {
        clearTimeout(wiggleTimer);
        clearTimeout(stopTimer);
      };
    }
  }, [wishlistCount]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isPagesDropdownOpen, setIsPagesDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const { scrollY } = useScroll();

  useEffect(() => {
    const unsubscribe = scrollY.on('change', latest => {
      setIsScrolled(prev => {
        const next = latest > 10;
        return prev !== next ? next : prev;
      });
    });
    return () => unsubscribe();
  }, [scrollY]);

  const navItems = NAV_CONTENT.items;

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') return location.pathname === '/';
      return location.pathname.startsWith(path.split('?')[0]);
    },
    [location.pathname]
  );

  useEffect(() => {
    const handle = requestAnimationFrame(() => {
      setIsMobileMenuOpen(false);
      setIsPagesDropdownOpen(false);
      setIsUserMenuOpen(false);
    });
    return () => cancelAnimationFrame(handle);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPagesDropdownOpen(false);
        setIsUserMenuOpen(false);
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-xl transition-all duration-300 dark:bg-neutral-950/80 ${
          isScrolled
            ? 'border-neutral-200/60 shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:border-neutral-800/80 dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)]'
            : 'border-white/20 shadow-[0_2px_20px_rgba(0,0,0,0.02)] dark:border-neutral-800/50'
        }`}>
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <div className="from-primary-100/40 absolute top-0 left-0 h-full w-full bg-gradient-to-b to-transparent" />
          <div className="bg-primary-200/10 absolute top-[-100%] left-[-10%] h-[300%] w-[40%] rounded-full blur-[80px]" />
        </div>

        <div className="container-toys relative z-10">
          <div className="flex h-16 items-center justify-between">
            <Link
              to="/"
              className="group flex items-center gap-2 transition-all duration-300 active:scale-95 lg:gap-3"
              aria-label={NAV_CONTENT.logo.altText}>
              <motion.div
                whileHover={{ rotate: [0, -5, 5, 0], scale: 1.05 }}
                className="relative h-14 w-14 items-center justify-center transition-shadow lg:h-16 lg:w-16">
                <LazyImage
                  src={NAV_CONTENT.logo.src}
                  alt={NAV_CONTENT.logo.altText}
                  priority={true}
                  className="h-full w-full"
                  imgClassName="object-contain!"
                />
              </motion.div>
              <div className="hidden flex-col sm:flex">
                <span className="font-display group-hover:text-primary-600 text-premium-gradient text-sm font-black tracking-tight transition-colors lg:text-xl">
                  {NAV_CONTENT.logo.text}
                </span>
                <span className="text-primary-500 hidden text-xs font-black tracking-widest uppercase lg:flex">
                  {NAV_CONTENT.logo.tagline}
                </span>
              </div>
            </Link>

            <nav aria-label="Main" className="hidden items-center gap-8 lg:flex">
              {navItems.map(item => (
                <div
                  key={item.label}
                  className="relative flex h-16 items-center"
                  onMouseEnter={() => item.children && setIsPagesDropdownOpen(true)}
                  onMouseLeave={() => item.children && setIsPagesDropdownOpen(false)}>
                  {item.children ? (
                    <div className="relative">
                      <button
                        aria-expanded={isPagesDropdownOpen}
                        type="button"
                        className={`font-display group flex items-center gap-1.5 text-sm font-bold tracking-wide transition-all duration-300 ${
                          isPagesDropdownOpen
                            ? 'text-primary-600'
                            : 'hover:text-primary-600 text-neutral-500'
                        }`}>
                        {item.label}
                        <motion.span
                          animate={{ rotate: isPagesDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.3, ease: 'backOut' }}>
                          <Icon name="ChevronDown" size={14} strokeWidth={2.5} />
                        </motion.span>
                      </button>

                      <AnimatePresence>
                        {isPagesDropdownOpen && (
                          <motion.div
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            variants={dropdownVariants}
                            className="absolute top-full left-1/2 z-50 mt-2 w-56 -translate-x-1/2 overflow-hidden rounded-3xl border border-white bg-white p-2 shadow-2xl ring-1 ring-neutral-100 backdrop-blur-2xl dark:border-neutral-800 dark:bg-neutral-950 dark:ring-neutral-800">
                            <div className="grid gap-1">
                              {item.children.map(child => (
                                <Link
                                  key={child.id}
                                  to={child.path}
                                  className="group/item hover:bg-primary-50 flex items-center gap-3 rounded-2xl px-3 py-2 transition-all"
                                  onClick={() => setIsPagesDropdownOpen(false)}>
                                  <div className="group-hover/item:text-primary-500 flex h-8 w-8 items-center justify-center rounded-xl bg-neutral-50 text-neutral-400 transition-colors group-hover/item:bg-white dark:bg-neutral-900 dark:text-neutral-500 dark:group-hover/item:bg-neutral-800">
                                    <Icon name="Sparkles" size={14} />
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="font-display group-hover/item:text-primary-600 text-sm font-bold text-neutral-700 transition-colors dark:text-neutral-300">
                                      {child.label}
                                    </span>
                                    <span className="text-xs font-medium text-neutral-400">
                                      {NAV_CONTENT.childDescription(child.label)}
                                    </span>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`font-display group relative flex h-full items-center text-sm font-bold tracking-wide transition-all duration-300 ${
                        isActive(item.path)
                          ? 'text-primary-600'
                          : 'hover:text-primary-600 text-neutral-500'
                      }`}>
                      {item.label}
                      {isActive(item.path) && (
                        <motion.div
                          layoutId="header-active-nav"
                          className="from-primary-400 to-primary-600 absolute right-0 bottom-4 left-0 h-[3px] rounded-t-full bg-gradient-to-r shadow-sm"
                        />
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <motion.button
                whileHover="hover"
                whileTap="tap"
                variants={interactiveHoverVariants}
                onClick={() => {
                  setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
                  playAudio.playTheme();
                }}
                className="hover:bg-primary-50 hover:text-primary-500 dark:hover:text-primary-400 flex h-10 w-10 items-center justify-center rounded-2xl text-neutral-500 transition-colors dark:hover:bg-neutral-800/60"
                aria-label="Toggle theme">
                <Icon
                  name={resolvedTheme === 'dark' ? 'Sun' : 'Moon'}
                  size={20}
                  strokeWidth={2.5}
                />
              </motion.button>

              <motion.button
                whileHover="hover"
                whileTap="tap"
                variants={interactiveHoverVariants}
                onClick={() => setIsSearchOpen(true)}
                className="hover:bg-primary-50 hover:text-primary-500 dark:hover:text-primary-400 flex h-10 w-10 items-center justify-center rounded-2xl text-neutral-500 transition-colors dark:hover:bg-neutral-800/60"
                aria-label={NAV_CONTENT.actions.search.ariaLabel}>
                <Icon name="Search" size={20} strokeWidth={2.5} />
              </motion.button>

              <motion.div
                animate={shouldWiggleWishlist ? 'wiggle' : 'default'}
                whileHover="hover"
                whileTap="tap"
                variants={{ ...interactiveHoverVariants, ...wiggleVariants }}>
                <Link
                  to="/wishlist"
                  className="hover:bg-primary-50 hover:text-primary-500 dark:hover:text-primary-400 relative flex h-10 w-10 items-center justify-center rounded-2xl text-neutral-500 transition-colors dark:hover:bg-neutral-800/60"
                  aria-label={NAV_CONTENT.actions.wishlist.ariaLabel}>
                  <Icon
                    name="Heart"
                    size={20}
                    strokeWidth={2.5}
                    className={wishlistCount > 0 ? 'fill-primary-500 text-primary-500' : ''}
                  />
                  <AnimatePresence>
                    {wishlistCount > 0 && (
                      <motion.span
                        variants={pulseVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="bg-primary-500 absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black text-white shadow-sm">
                        {wishlistCount}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
              </motion.div>

              <motion.div
                animate={shouldWiggleCart ? 'wiggle' : 'default'}
                whileHover="hover"
                whileTap="tap"
                variants={{ ...interactiveHoverVariants, ...wiggleVariants }}>
                <Link
                  to="/cart"
                  className="hover:bg-primary-50 hover:text-primary-500 dark:hover:text-primary-400 relative flex h-10 w-10 items-center justify-center rounded-2xl text-neutral-500 transition-colors dark:hover:bg-neutral-800/60"
                  aria-label={NAV_CONTENT.actions.cart.ariaLabel}>
                  <Icon name="ShoppingBag" size={20} strokeWidth={2.5} />
                  <AnimatePresence>
                    {cartCount > 0 && (
                      <motion.span
                        variants={pulseVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="bg-primary-600 absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black text-white shadow-sm">
                        {cartCount}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
              </motion.div>

              <div className="ml-2 hidden lg:block">
                {isAuthenticated ? (
                  <div className="relative">
                    <motion.button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      whileHover="hover"
                      whileTap="tap"
                      variants={interactiveHoverVariants}
                      aria-expanded={isUserMenuOpen}
                      aria-label="User menu"
                      className="group hover:border-primary-100 flex items-center gap-3 rounded-full border border-neutral-100 bg-white p-1 pr-4 shadow-sm transition-all hover:shadow-md dark:border-neutral-800 dark:bg-neutral-950">
                      <div className="h-8 w-8 overflow-hidden rounded-full bg-neutral-100">
                        {user?.avatar ? (
                          <LazyImage
                            src={user.avatar}
                            alt={user.name}
                            priority={true}
                            className="h-full w-full"
                          />
                        ) : (
                          <div className="from-primary-400 to-primary-600 flex h-full w-full items-center justify-center bg-gradient-to-br text-white">
                            <Icon name="User" size={16} />
                          </div>
                        )}
                      </div>
                      <span className="font-display text-sm font-bold text-neutral-700 dark:text-neutral-200">
                        {user?.name?.split(' ')[0]}
                      </span>
                    </motion.button>

                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          variants={dropdownVariants}
                          className="absolute top-full right-0 z-50 mt-2 w-56 overflow-hidden rounded-3xl border border-white bg-white p-2 shadow-2xl ring-1 ring-neutral-100 backdrop-blur-2xl dark:border-neutral-800 dark:bg-neutral-950 dark:ring-neutral-800">
                          <Link
                            to="/account"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="hover:bg-primary-50 hover:text-primary-600 flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-neutral-700 transition-all dark:text-neutral-300 dark:hover:bg-neutral-800">
                            <Icon name="User" size={16} /> {NAV_CONTENT.actions.myAccount.label}
                          </Link>
                          <div className="mx-2 my-1 h-px bg-neutral-100 dark:bg-neutral-800" />
                          <Button
                            onClick={() => {
                              logout();
                              setIsUserMenuOpen(false);
                            }}
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start gap-3 rounded-2xl px-4 py-3 text-sm text-red-500 hover:bg-red-50 hover:text-red-500">
                            <Icon name="LogOut" size={16} /> {NAV_CONTENT.actions.signOut.label}
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="font-display hover:bg-primary-700 bg-primary-600 flex h-10 items-center justify-center rounded-full px-6 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg">
                    {NAV_CONTENT.actions.signIn.label}
                  </Link>
                )}
              </div>

              <motion.button
                onClick={() => setIsMobileMenuOpen(true)}
                whileHover="hover"
                whileTap="tap"
                variants={interactiveHoverVariants}
                className="flex h-10 w-10 items-center justify-center rounded-2xl text-neutral-700 transition-colors hover:bg-neutral-50 lg:hidden dark:text-neutral-300 dark:hover:bg-neutral-800"
                aria-label="Open navigation menu">
                <Icon name="Menu" size={24} strokeWidth={2.5} />
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      <React.Suspense fallback={null}>
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          isAuthenticated={isAuthenticated}
          user={user}
          logout={logout}
        />
      </React.Suspense>

      <React.Suspense fallback={null}>
        <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      </React.Suspense>
    </>
  );
};

export default Header;
